import React, { useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppContext } from '@prisjakt/ui-app-context';
import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import { ExpandableGrid } from 'src/components/expandable-grid';
import { ProductGridCard } from 'src/components/product-card-new';
import { productNavigation } from '../../areas/product/actions/product';
import { NEW_PRODUCT_CLICK } from '../../globals/helpers/navigation-action';
import { Product } from 'src/types';
import { useViewportContext } from 'src/components/viewport/context';
import { InternalLink } from 'src/ui/link';
import {
  CaptionText,
  FontColor,
  TitleMediumText,
} from '@prisjakt/ui/text/variants';
import AlertButton from 'src/components/new-product-releases/components/alert-button';
import { CallToActionButton, FlexColumn } from './styles';

type Props = {
  products: Product[];
  title: string;
  subTitle?: string;
  linkTitle?: string;
  linkPath?: string;
  shouldLazyLoadImages?: boolean;
  dataTestType?: string;
  ssrExpanded?: boolean;
  showCreateAlertButton?: boolean;
  showLinkAsButton?: boolean;
  productNavigationAction?: string;
  onHeaderLinkClick?: (data: { link: string }) => void;
  onProductCardClick?: (data: { index: number; link: string }) => void;
};

const ExpandableProductCardGrid: React.FC<Props> = props => {
  const {
    products,
    title,
    subTitle = null,
    linkTitle = null,
    linkPath = null,
    shouldLazyLoadImages,
    dataTestType,
    ssrExpanded,
    showCreateAlertButton,
    showLinkAsButton,
    productNavigationAction,
    onHeaderLinkClick,
    onProductCardClick,
  } = props;
  const { isTablet } = useViewportContext();
  const dispatch = useDispatch();
  const { t } = useContext(AppContext);

  const productCards = useMemo(() => {
    return products.map((product, index) => (
      <ProductGridCard
        key={product.id}
        brand={
          product.brand && {
            logo: product.brand.logo,
            name: product.brand.name,
          }
        }
        category={
          product.category && {
            name: product.category.name,
          }
        }
        imageSrc={product.media.first}
        shouldLazyLoadImage={shouldLazyLoadImages}
        link={product.pathName}
        name={product.name}
        price={product.priceSummary.regular || product.priceSummary.alternative}
        onClick={() => {
          dispatch(
            productNavigation(
              productNavigationAction ?? NEW_PRODUCT_CLICK,
              index + 1
            )
          );
          onProductCardClick?.({ index, link: product.pathName });
        }}
        rating={product.userReviewSummary?.rating}
        reviewsCount={product.userReviewSummary?.count}
        background
        FooterArea={
          showCreateAlertButton ? (
            <AlertButton
              product={product}
              text={t('product', 'alert.create-button')}
              type="PRICE_DROPS"
            />
          ) : null
        }
      />
    ));
  }, [
    dispatch,
    onProductCardClick,
    productNavigationAction,
    products,
    shouldLazyLoadImages,
    showCreateAlertButton,
    t,
  ]);

  let headerLink = null;
  if (linkTitle && linkPath) {
    headerLink = showLinkAsButton ? (
      <CallToActionButton component={<Link to={linkPath} />}>
        {linkTitle}
      </CallToActionButton>
    ) : (
      <InternalLink
        to={linkPath}
        onClick={() => {
          onHeaderLinkClick?.({ link: linkPath });
        }}
      >
        <TitleMediumText color={FontColor.PRIMARY}>{linkTitle}</TitleMediumText>
      </InternalLink>
    );
  }

  return (
    <div>
      {products.length > 0 && (
        <Panel
          containerProps={
            dataTestType
              ? {
                  'data-test': 'Panel',
                  'data-test-type': dataTestType,
                }
              : ''
          }
        >
          <PanelHeader>
            <FlexColumn>
              <PanelTitle>{title}</PanelTitle>
              {subTitle && <CaptionText>{subTitle}</CaptionText>}
            </FlexColumn>
            {headerLink}
          </PanelHeader>
          <div>
            <ExpandableGrid
              limit={isTablet ? 3 : 4}
              expandText={t('common', 'button.show-more')}
              collapseText={t('common', 'button.show-less')}
              items={productCards}
              cssGrid
              ssrExpanded={ssrExpanded}
            />
          </div>
        </Panel>
      )}
    </div>
  );
};

export default ExpandableProductCardGrid;
